import React, { useState } from "react";
import { apiFetch } from "../../services/api";
import LoadingButton from "../../components/common/LoadingButton";
import V2AuthLayout from "../components/V2AuthLayout";
import { useV2Theme } from "../theme/ThemeProvider";

export default function V2EmailSentPage({ email, initialEmailSent = false, onBackToLogin }) {
  const { tokens } = useV2Theme();
  const [emailSent, setEmailSent] = useState(initialEmailSent);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleSend = async () => {
    if (!email) {
      setResendMessage("Email address not available");
      return;
    }
    if (resendLoading) return;

    setResendLoading(true);
    setResendMessage("");
    try {
      const response = await apiFetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.sent === true) {
        setEmailSent(true);
        setResendMessage("Verification email sent! Please check your inbox.");
        setTimeout(() => setResendMessage(""), 5000);
      } else if (response.ok && data.sent === false) {
        setResendMessage(
          "We could not send the email right now. Please try again in a moment."
        );
      } else {
        setResendMessage(
          data.detail?.message ||
            data.detail ||
            "Failed to send verification email. Please try again."
        );
      }
    } catch {
      setResendMessage("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const linkBtn = "text-sm font-medium text-blue-700 hover:text-blue-600";
  const sendLabel = emailSent ? "Resend verification email" : "Send verification email";

  return (
    <V2AuthLayout
      title="Check your email"
      subtitle={
        emailSent
          ? "We sent a verification link to complete your registration."
          : "Your account was created. Send a verification link to your inbox to continue."
      }
    >
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        {emailSent ? (
          <p>
            Open the message at{" "}
            <span className="font-medium text-slate-900">{email || "your inbox"}</span> and confirm
            your address before signing in. Check spam or junk if you do not see it within a few
            minutes.
          </p>
        ) : (
          <p>
            We could not confirm delivery to{" "}
            <span className="font-medium text-slate-900">{email || "your address"}</span>. Use the
            button below to send the verification link.
          </p>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <LoadingButton
          type="button"
          onClick={handleSend}
          loading={resendLoading}
          loadingLabel="Sending…"
          className={`w-full ${
            emailSent ? tokens.button.secondary : tokens.button.primary
          } disabled:opacity-50`}
        >
          {sendLabel}
        </LoadingButton>
        {resendMessage ? (
          <p
            className={`text-xs text-center ${
              resendMessage.includes("sent") ? "text-emerald-700" : "text-rose-700"
            }`}
          >
            {resendMessage}
          </p>
        ) : null}
        <button type="button" onClick={onBackToLogin} className={`w-full text-center ${linkBtn}`}>
          ← Back to sign in
        </button>
      </div>
    </V2AuthLayout>
  );
}
